import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { useAutoRefresh } from "@/lib/useAutoRefresh";
import { ListingCard } from "@/components/ListingCard";
import { Filters } from "@/components/Filters";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SlidersHorizontal, Search, PackageOpen } from "lucide-react";
import { categoryIcon } from "@/lib/categoryIcons";

const emptyFilters = { category: "", region: "", state: "", location: "", maxPrice: 20000, delivery: false };

export default function Catalog() {
  const { t } = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("recommended");
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [filters, setFilters] = useState({ ...emptyFilters, category: searchParams.get("category") || "" });

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/listings/regions").then((r) => setRegions(r.data));
  }, []);

  useEffect(() => {
    setFilters((f) => ({ ...f, category: searchParams.get("category") || "" }));
    setQ(searchParams.get("q") || "");
  }, [searchParams]);

  const fetchListings = useCallback((withSpinner = false) => {
    if (withSpinner) setLoading(true);
    const params = { sort };
    if (filters.category) params.category = filters.category;
    if (filters.region) params.region = filters.region;
    if (filters.state) params.state = filters.state;
    if (filters.location) params.location = filters.location;
    if (filters.maxPrice) params.max_price = filters.maxPrice;
    if (filters.delivery) params.delivery = true;
    if (q) params.q = q;
    api.get("/listings", { params }).then((r) => setListings(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [filters, sort, q]);

  useEffect(() => {
    const timer = setTimeout(() => fetchListings(true), 200);
    return () => clearTimeout(timer);
  }, [fetchListings]);

  useAutoRefresh(() => fetchListings(false), 20000);

  const activeCategoryName = useMemo(
    () => (filters.category ? t(`categories.${filters.category}`) : null),
    [filters.category, t]
  );

  const reset = () => { setFilters(emptyFilters); setQ(""); setSearchParams({}); };

  const pickCategory = (slug) => {
    const nextCat = filters.category === slug ? "" : slug;
    setFilters((f) => ({ ...f, category: nextCat }));
    const sp = {};
    if (nextCat) sp.category = nextCat;
    if (q) sp.q = q;
    setSearchParams(sp);
  };

  const filtersEl = <Filters categories={categories} regions={regions} filters={filters} onChange={setFilters} onReset={reset} />;

  return (
    <div className="container-page py-8 sm:py-12">
      <div className="mb-6">
        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight text-secondary">
          {activeCategoryName || t("catalog.title")}
        </h1>
        <p className="text-muted-foreground mt-2">
          {q ? `${t("catalog.resultsFor")} « ${q} »` : t("catalog.subtitle")}
        </p>
      </div>

      <div className="relative mb-6 max-w-xl">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
        <input data-testid="catalog-search-input" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={t("catalog.searchPlaceholder")}
          className="w-full h-12 pl-12 pr-4 rounded-full bg-card border border-border focus:border-primary outline-none text-base transition-colors" />
      </div>

      {/* Category icons */}
      {categories.length > 0 && (
        <div className="mb-8 grid grid-cols-3 sm:grid-cols-6 gap-2.5" data-testid="market-categories">
          {categories.map((c) => {
            const active = filters.category === c.slug;
            return (
              <button key={c.slug} type="button" onClick={() => pickCategory(c.slug)} data-testid={`market-cat-${c.slug}`}
                className={`min-h-[96px] rounded-2xl border-2 p-2 flex flex-col items-center justify-center gap-1.5 transition-colors active:scale-95 ${active ? "border-secondary bg-secondary/10 ring-2 ring-secondary/20" : "border-border bg-card hover:border-secondary/50"}`}>
                <img src={categoryIcon(c.slug)} alt={t(`categories.${c.slug}`)} className="h-12 w-12 object-contain" loading="lazy" />
                <span className="text-[11px] font-semibold text-secondary text-center leading-tight">{t(`categories.${c.slug}`)}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex gap-8">
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="sticky top-24 rounded-2xl bg-card border border-border p-6">{filtersEl}</div>
        </aside>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-5 gap-3">
            <div className="flex items-center gap-3">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="lg:hidden rounded-full" data-testid="mobile-filters-btn">
                    <SlidersHorizontal className="h-4 w-4 mr-2" /> {t("catalog.filters")}
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80 overflow-y-auto"><div className="mt-8">{filtersEl}</div></SheetContent>
              </Sheet>
              <span className="text-sm text-muted-foreground" data-testid="results-count">
                {loading ? "…" : `${listings.length} ${t("catalog.results")}`}
              </span>
            </div>

            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-48 rounded-full" data-testid="sort-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="recommended">{t("catalog.sortRecommended")}</SelectItem>
                <SelectItem value="recent">{t("catalog.sortRecent")}</SelectItem>
                <SelectItem value="rating">{t("catalog.sortRecent") === "Plus récents" ? "Mieux notés" : "Top rated"}</SelectItem>
                <SelectItem value="price_asc">{t("catalog.sortPriceAsc")}</SelectItem>
                <SelectItem value="price_desc">{t("catalog.sortPriceDesc")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-80 rounded-2xl" />)}
            </div>
          ) : listings.length === 0 ? (
            <div className="text-center py-20 rounded-2xl bg-card border border-dashed border-border" data-testid="empty-state">
              <PackageOpen className="h-10 w-10 mx-auto text-muted-foreground" />
              <p className="mt-4 font-heading font-semibold text-lg text-secondary">{t("catalog.emptyTitle")}</p>
              <p className="text-muted-foreground text-sm mt-1">{t("catalog.emptyText")}</p>
              <Button onClick={reset} className="mt-5 rounded-full" data-testid="empty-reset-btn">{t("common.reset")}</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {listings.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
